import axios from 'axios';
import { snakeCase } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import {
  useAdjustmentUploadDoneMutation,
  useGetAdjustmentUploadUrlMutation,
} from '../../billing-tool/types';
import { BULK_ADJUSTMENTS_CATEGORY } from '../components/bulk-adjustments';
import { appendUploadStatus } from '../components/upload-status-storage';

export const ADJUSTMENT_UPLOAD_FALLBACK_ERROR = 'Error uploading file';

// Browsers ignore the text, but returnValue has to be set for the dialog.
//
// One shared handler is enough. Accounting uploads one adjustment file per
// quarter. A second file for the same quarter only follows a bad upload, and
// that correction is made by hand outside this page.
const warnBeforeUnload = (event: BeforeUnloadEvent): void => {
  event.preventDefault();
  event.returnValue = '';
};

interface UploadedFile {
  objectKey: string;
  fileName: string;
}

export interface AdjustmentUploadResult {
  taskId?: string;
  errorMessage?: string;
}

export interface UseAdjustmentUploadReturnType {
  uploadAdjustmentFile: (file: File) => Promise<AdjustmentUploadResult>;
  isUploading: boolean;
  progress: number;
}

export const useAdjustmentUpload = (): UseAdjustmentUploadReturnType => {
  const [getUploadUrl] = useGetAdjustmentUploadUrlMutation();
  const [uploadDone] = useAdjustmentUploadDoneMutation();
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  // Set once the file reaches S3. A retry after a failed upload-done call
  // finishes this key instead of uploading the file again. The name is kept
  // with it so a retry on a different file starts over.
  const [uploaded, setUploaded] = useState<UploadedFile | undefined>();

  // The upload outlives the component, so the state updates after each await
  // are skipped once it is gone. React 17 warns about them otherwise.
  const isMounted = useRef(true);

  useEffect(
    () => (): void => {
      isMounted.current = false;
    },
    [],
  );

  // The upload is not aborted when the component unmounts. A part-written
  // file starts no workflow, so stopping it would only lose the task ID that
  // Check Status needs.
  const uploadAdjustmentFile = async (
    file: File,
  ): Promise<AdjustmentUploadResult> => {
    const pending = uploaded?.fileName === file.name ? uploaded : undefined;

    setIsUploading(true);
    setProgress(pending ? 100 : 0);
    window.addEventListener('beforeunload', warnBeforeUnload);

    try {
      let objectKey = pending?.objectKey;

      if (objectKey === undefined) {
        const urlResponse = await getUploadUrl({
          variables: {
            input: {
              fileName: file.name,
              fileSize: file.size,
              fileCategory: snakeCase(BULK_ADJUSTMENTS_CATEGORY),
            },
          },
        });

        const uploadUrl = urlResponse.data?.getAdjustmentUploadUrl;

        if (!uploadUrl) {
          throw new Error(ADJUSTMENT_UPLOAD_FALLBACK_ERROR);
        }

        await axios.put(uploadUrl.url, file, {
          headers: uploadUrl.headers,
          onUploadProgress: ({ loaded, total }): void => {
            if (total && isMounted.current) {
              setProgress(Math.round((loaded / total) * 100));
            }
          },
        });

        objectKey = uploadUrl.objectKey;

        if (isMounted.current) {
          setUploaded({ objectKey, fileName: file.name });
        }
      }

      const doneResponse = await uploadDone({
        variables: {
          input: { objectKey, fileName: file.name },
        },
      });

      const taskId = doneResponse.data?.adjustmentUploadDone?.taskId;

      if (!taskId) {
        throw new Error(ADJUSTMENT_UPLOAD_FALLBACK_ERROR);
      }

      // Written here rather than in a render effect so the entry still lands
      // when the user has navigated away from the page.
      appendUploadStatus({
        workflowId: taskId,
        fileName: file.name,
        fileLink: '',
        fileType: 'CSV File',
      });

      if (isMounted.current) {
        setUploaded(undefined);
      }

      return { taskId };
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : ADJUSTMENT_UPLOAD_FALLBACK_ERROR;

      return { errorMessage };
    } finally {
      window.removeEventListener('beforeunload', warnBeforeUnload);

      if (isMounted.current) {
        setIsUploading(false);
      }
    }
  };

  return { uploadAdjustmentFile, isUploading, progress };
};
